import React, { useEffect, useRef, useState } from 'react'
import {
  Button,
  CircularProgress,
  Divider,
  Link,
  Typography,
} from '@material-ui/core'
import {
  CurrencyList,
  currencyTransform,
  Locale,
} from '../../../../utils/currency-transform'
import { ItemCondition, ItemState } from '../../../../models'
import { useNavigate, useParams } from 'react-router-dom'
import { transformToMultiLine } from '../../../../utils/strings'
import { useTranslation } from 'react-i18next'
import { useScreenSize } from '../../../../hooks/use-screen-size'
import Skeleton from '@material-ui/lab/Skeleton'
import Alert from '@material-ui/lab/Alert'
import { DeliveryType } from '../../../../enums'
import { axiosInstance } from '../../../../api'
import { useAppDialog } from '../../../../hooks/use-app-dialog'
import { AppDialogVariants } from '../../../../store/app-dialog/types'
import { InfoComponentProps } from './info-component.types'
import { useInfoComponentStyles } from '../../../listed-item-details-page/listed-item-details-component/info-component/info-component.styles'
import { useOwnItemInfoActionsStyles } from './info-component-actions.styles'
import { useActionButtonRowStyles } from '../../../../shared/action-button-row.styles'
import { ExpandableText } from './expandable-text'
import { getDateByState, getDateTitle } from '../../../../utils/item-details'
import { useDispatch } from 'react-redux'
import { trackException } from '../../../../store/app-insights/reducer'
import EditIcon from '@material-ui/icons/Edit'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'

const expandableTextMinimumHeight = 404

export const InfoComponent: React.FC<InfoComponentProps> = (props) => {
  const classes = useInfoComponentStyles()
  const actionClasses = useOwnItemInfoActionsStyles()
  const actionRowClasses = useActionButtonRowStyles()
  const navigate = useNavigate()
  const {
    name,
    description = '',
    currentPrice,
    itemCondition,
    locale = Locale.US,
    currency = CurrencyList.USD,
    nonprofitName,
    nonprofit,
    listedUtc,
    soldUtc,
    createdUtc,
    donorName,
    state,
    city,
    region,
    loading,
    isShippingAvailable,
    setInStoreDeliveryType,
    onChangeMode,
    updateState,
  } = props

  const { t } = useTranslation()
  const screenSize = useScreenSize()
  const dispatch = useDispatch()
  const [isLongText, setIsLongText] = useState<boolean>(false)
  const textHolder = useRef<HTMLDivElement>(null)
  const [orderId, setOrderId] = useState<number | string>('')
  const { itemId } = useParams()
  const { setAppDialog, setIsShow } = useAppDialog()

  const externalId = nonprofit?.externalId ?? '0'
  const nonprofitCardId = `/external/${externalId}?externalId=${externalId}`

  useEffect(() => {
    if (!itemId) return
    axiosInstance
      .get(`/listed-items/donor/${itemId}`)
      .then(({ data }) => {
        setOrderId(data.orderId)
      })
      .catch((e) =>
        dispatch(trackException({ e, source: 'ListedItemsDonorOrderId' }))
      )
  }, [itemId, dispatch])

  const checkLongText = () => {
    if (!textHolder.current) return
    const textHolderHeight = textHolder.current.offsetHeight
    textHolderHeight > expandableTextMinimumHeight
      ? setIsLongText(true)
      : setIsLongText(false)
  }

  useEffect(checkLongText, [screenSize, description, loading])

  useEffect(() => {
    setInStoreDeliveryType(DeliveryType.Shipping)
  }, [isShippingAvailable, setInStoreDeliveryType])

  const changeStateHandler = (nextState: ItemState) => {
    setAppDialog({
      isShow: true,
      variant: AppDialogVariants.warning,
      title: t('editItemDetailsPage.unActiveItem.title'),
      message:
        nextState === ItemState.Unlisted
          ? t('editItemDetailsPage.unActiveItem.unList')
          : t('editItemDetailsPage.unActiveItem.toDraft'),
      actions: (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsShow(false)}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              updateState(nextState)
              setIsShow(false)
            }}
          >
            Ok
          </Button>
        </>
      ),
    })
  }

  const toOrder = () => {
    if (!orderId) {
      return
    }
    navigate(`/selling/order/${orderId}`)
  }

  const renderDetailsRows = () => (
    <div className={classes.detailsGrid}>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.nonprofit')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {nonprofitName ? (
            <Link
              href={nonprofitCardId}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              variant="body2"
              className={classes.detailValue}
              underline="hover"
            >
              {nonprofitName}
            </Link>
          ) : (
            <Skeleton animation="wave" width="75%" />
          )}
        </Typography>
      </div>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.donorName')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {donorName ? donorName : <Skeleton animation="wave" width="70%" />}
        </Typography>
      </div>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.location')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {city ? (
            `${city}, ${region}`
          ) : (
            <Skeleton animation="wave" width="70%" />
          )}
        </Typography>
      </div>
      <div className={classes.detailRow}>
        <Typography variant="body2" color="textSecondary" component="span">
          {t('itemDetails.condition')}
        </Typography>
        <Typography
          variant="body2"
          component="span"
          className={classes.detailValue}
        >
          {itemCondition != undefined ? (
            itemCondition === ItemCondition.New ? (
              t('itemDetails.new')
            ) : (
              t('itemDetails.used')
            )
          ) : (
            <Skeleton animation="wave" width="70%" />
          )}
        </Typography>
      </div>
    </div>
  )

  const renderSellerActions = () => {
    if (loading) {
      return (
        <div className={actionClasses.loadingActions}>
          <CircularProgress size={28} />
        </div>
      )
    }

    return (
      <div className={actionClasses.actionsSection}>
        {state === ItemState.Active && (
          <>
            <div className={actionRowClasses.equalWidthRow}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => changeStateHandler(ItemState.Draft)}
                className={actionClasses.actionBtn}
              >
                {t('editItemDetailsPage.setToDraft')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onChangeMode(true)}
                className={actionClasses.actionBtn}
                aria-label={t('editItemDetailsPage.edit')}
                title={t('editItemDetailsPage.edit')}
              >
                <EditIcon fontSize="small" />
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => changeStateHandler(ItemState.Unlisted)}
                className={actionClasses.actionBtn}
                aria-label={t('editItemDetailsPage.unlist')}
                title={t('editItemDetailsPage.unlist')}
              >
                <DeleteOutlineIcon fontSize="small" />
              </Button>
            </div>
          </>
        )}

        {state === ItemState.Draft && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => onChangeMode(true)}
            className={actionClasses.actionBtn}
          >
            {t('editItemDetailsPage.editDraft')}
          </Button>
        )}

        {state === ItemState.Sold && (
          <>
            <Button
              onClick={() => toOrder()}
              variant="contained"
              color="primary"
              className={actionClasses.actionBtn}
            >
              {t('editItemDetailsPage.goToOrder')}
            </Button>
            <Alert severity="info" className={classes.soldStateAlert}>
              {t('editItemDetailsPage.thisItemWasSold')}
            </Alert>
          </>
        )}

        {state === ItemState.Unlisted && (
          <Typography
            variant="body1"
            color="textSecondary"
            component="span"
            className={classes.wasSoldLabel}
          >
            {t('editItemDetailsPage.unlistedItem')}
          </Typography>
        )}
      </div>
    )
  }

  return (
    <div className={classes.infoBlock}>
      <Typography
        className={classes.listingTitle}
        variant="h5"
        component="h1"
      >
        {name ? name : <Skeleton animation="wave" width="70%" />}
      </Typography>
      <div className={classes.listedTimeBlock}>
        {listedUtc || createdUtc ? (
          <>
            <Typography component="span" variant="body2" color="textSecondary">
              {getDateTitle(t, state)}:{' '}
            </Typography>
            <Typography
              component="span"
              variant="body2"
              color="textPrimary"
              className={classes.listedDateValue}
            >
              {getDateByState({ listedUtc, createdUtc, soldUtc }, state)}
            </Typography>
          </>
        ) : (
          <Skeleton animation="wave" width="50%" />
        )}
      </div>

      {!loading && (
        <>
          <div className={classes.ctaSection}>
            <div className={classes.priceRow}>
              <Typography variant="h6" component="div">
                {currencyTransform(currentPrice, locale, {
                  style: 'currency',
                  currency,
                })}
              </Typography>
            </div>
            {renderSellerActions()}
          </div>

          <Divider className={classes.divider} />

          <Typography
            className={classes.sectionTitle}
            variant="subtitle1"
            component="h2"
          >
            {t('itemDetails.detailsHeading')}
          </Typography>
          {renderDetailsRows()}

          <Divider className={classes.divider} />

          {description ? (
            <Typography
              className={classes.sectionTitle}
              variant="subtitle1"
              component="h3"
            >
              {t('itemDetails.description')}
            </Typography>
          ) : (
            <Skeleton animation="wave" width="40%" />
          )}
          <div>
            <Typography
              className={classes.descriptionMeasure}
              ref={textHolder}
              variant="body1"
              color="textSecondary"
              component="p"
            >
              {transformToMultiLine(description)}
            </Typography>
            {!isLongText && description && (
              <Typography variant="body1" color="textSecondary" component="p">
                {transformToMultiLine(description)}
              </Typography>
            )}
            {isLongText && <ExpandableText description={description} />}
            {!description && !loading && (
              <Typography
                variant="body2"
                component="span"
                className={actionClasses.noDescription}
              >
                No description
              </Typography>
            )}
            {!description && loading && (
              <>
                <Skeleton animation="wave" width="80%" />
                <Skeleton animation="wave" width="85%" />
                <Skeleton animation="wave" width="90%" />
                <Skeleton animation="wave" width="75%" />
              </>
            )}
          </div>
        </>
      )}

      {loading && (
        <>
          <div className={classes.ctaSection}>
            <div className={classes.loadingPriceRow}>
              <Skeleton animation="wave" height={30} width="40%" />
            </div>
            <div className={actionClasses.loadingActions}>
              <CircularProgress size={28} />
            </div>
          </div>
          <Divider className={classes.loadingDivider} />
          <Skeleton
            animation="wave"
            height={22}
            width="34%"
            className={classes.loadingSkeletonHeading}
          />
          <div className={classes.loadingDetailsGrid}>
            {[0, 1, 2, 3].map((key) => (
              <div key={key} className={classes.loadingDetailRow}>
                <Skeleton animation="wave" height={20} width="72%" />
                <Skeleton animation="wave" height={20} width="88%" />
              </div>
            ))}
          </div>
          <Divider className={classes.loadingDivider} />
          <Skeleton
            animation="wave"
            height={22}
            width="40%"
            className={classes.loadingSkeletonHeading}
          />
          <div className={classes.loadingDescriptionStack}>
            <Skeleton animation="wave" height={22} width="100%" />
            <Skeleton animation="wave" height={22} width="96%" />
            <Skeleton animation="wave" height={22} width="91%" />
            <Skeleton animation="wave" height={22} width="94%" />
            <Skeleton animation="wave" height={22} width="68%" />
            <Skeleton animation="wave" height={22} width="84%" />
          </div>
        </>
      )}
    </div>
  )
}
